"use client";

// PoweredByIndexing — a reusable "grounded in a real-time index" band for app pages.
// Mixes a split layout (copy + cross-links) with animated sparkline stat cards.

import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { ArrowRight, Bug, Layers, Puzzle } from "lucide-react";
import Link from "next/link";

type Kind = "line" | "bar";

type Stat = { display: string; label: string; series: number[] | null };

/**
 * WHAT THESE CARDS SHOW, AND WHAT THEY USED TO.
 *
 * They were four invented figures dressed as measurements: "100%", "4 content types",
 * "3 sources" and an infinity glyph, each paired with a hand-written `series` array so the
 * sparkline had a pleasing upward shape. None of it was read from anything. A stat card with a
 * fabricated trend line beside it is the single most corrosive thing a page like this can carry,
 * because the live figures elsewhere on the site are real and a reader who catches one invention
 * is right to stop believing those too.
 *
 * Every value here is now fetched from the running index and passed in from the server. When a
 * figure is unavailable the card is dropped rather than defaulted, and the sparkline is drawn
 * ONLY where a real series exists - never synthesised to fill the space.
 */
export type IndexingFigures = {
  /** Formatted storage-per-page, e.g. "1.6 KB". A ratio, which is publishable; totals are not. */
  perPage?: string | null;
  collections?: number | null;
  nodes?: number | null;
  onDemandSeconds?: number | null;
  /** Real 30-day point counts for the busiest corpus, for the one sparkline we can honestly draw. */
  series?: number[] | null;
};

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function buildStats(f?: IndexingFigures | null): Stat[] {
  if (!f) return [];
  const out: Stat[] = [];
  /* NO PAGE TOTAL. It is deliberately withheld while the index is being built — see the note in
     `web-index-hero-stats.tsx` ("the page total ... is no longer printed"), and the policy stated
     on the /api/index/stats and /quality rewrites in next.config.ts: ratios are published, the
     totals they came from are not. This card showed `1.7M pages` between 2026-09-05 and -06, which
     broke that rule; storage-per-page is the published figure and makes the sharper point anyway. */
  if (f.perPage)
    out.push({ display: f.perPage, label: "Storage per page read and kept searchable", series: f.series ?? null });
  if (typeof f.collections === "number" && f.collections > 0)
    out.push({ display: String(f.collections), label: "Collections, each filed as the kind of thing it is", series: null });
  if (typeof f.nodes === "number" && f.nodes > 0)
    out.push({ display: String(f.nodes), label: "Machines reading the web right now", series: null });
  if (typeof f.onDemandSeconds === "number" && f.onDemandSeconds > 0)
    out.push({ display: `${f.onDemandSeconds < 10 ? f.onDemandSeconds.toFixed(1) : Math.round(f.onDemandSeconds)}s`, label: "From asking for a page to finding it in search", series: null });
  return out;
}

function Spark({ kind, series }: { kind: Kind; series: number[] }) {
  const w = 84, h = 28;
  const max = Math.max(...series), min = Math.min(...series);
  const span = max - min || 1;
  if (kind === "line") {
    const pts = series
      .map((v, i) => `${((i / (series.length - 1)) * w).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`)
      .join(" ");
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden>
        <polyline points={pts} className="stroke-accent" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  const bw = w / series.length;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      {series.map((v, i) => {
        const bh = ((v - min) / span) * (h - 2) + 2;
        return <rect key={i} x={i * bw + 1} y={h - bh} width={bw - 2} height={bh} rx={1} className="fill-foreground/25 last:fill-accent" />;
      })}
    </svg>
  );
}

function ChartCard({ stat, start, i }: { stat: Stat; start: boolean; i: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={start ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
      className="lift-card flex flex-col justify-between gap-4 rounded-2xl border hairline bg-card p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="display-text tabular-num text-3xl leading-none tracking-tight">
          {stat.display}
        </div>
        {/* Only where a real series was supplied. A sparkline with no data behind it is a lie
            drawn in a shape people trust, so the card simply goes without one. */}
        {stat.series && stat.series.length > 1 && <Spark kind="line" series={stat.series} />}
      </div>
      <p className="text-[13px] leading-snug text-foreground/55">{stat.label}</p>
    </motion.div>
  );
}

const LINKS = [
  { href: "/crawler", label: "Web crawler", icon: Bug },
  { href: "/web-index", label: "Indexer", icon: Layers },
  { href: "/extension", label: "Extension", icon: Puzzle },
];

export function PoweredByIndexing({ appName, figures }: { appName?: string; figures?: IndexingFigures | null }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const stats = buildStats(figures);
  return (
    <section ref={ref} className="relative w-full overflow-hidden bg-background text-foreground">
      <div aria-hidden className="dotted-bg absolute inset-0 opacity-40" />
      <div className="container-x relative z-10 py-14 md:py-20 grid items-center gap-12 lg:grid-cols-2">
        {/* Left — copy + cross-links */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="eyebrow text-accent">Powered by askFinz indexing</p>
          <h2 className="display-text mt-4 text-3xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
            {appName ? <>Everything {appName} shows is</> : <>Everything here is</>}{" "}
            <span className="font-serif italic">grounded in a real-time index.</span>
          </h2>
          <p className="mt-5 max-w-md text-foreground/65 leading-relaxed">
            Behind the scenes, a web crawler and the browser extension read pages
            across the web, and an indexer turns them into searchable knowledge
            the moment they&#39;re seen &mdash; including modern apps and
            bot-walled sites others can&#39;t reach.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {LINKS.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="group inline-flex items-center gap-2 rounded-full border hairline-strong px-4 py-2 text-sm font-medium hover:bg-foreground/5"
              >
                <l.icon className="h-4 w-4 opacity-70" />
                {l.label}
                <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all" />
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Right — live figures from the running index. Renders nothing at all when the index is
            unreachable: the left-hand copy stands on its own, and an empty grid of dashes would
            say "broken" where the point being made is "live". */}
        {stats.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.map((st, i) => (
              <ChartCard key={st.label} stat={st} start={inView} i={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
